import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  confirmSignUp,
  fetchAuthSession,
  signIn,
  signOut,
  signUp,
} from 'aws-amplify/auth'
import { configureAmplify } from '../lib/amplify'
import { MaruyaApiError, maruyaApi } from '../lib/maruya-api'
import { normalizeMeResponse } from '../lib/normalize-me-response'
import type { MeResponse, ProvisionResponse } from '../types/api'

export type AuthPhase =
  | 'loading'
  | 'unauthenticated'
  | 'needs_invite'
  | 'email_required'
  | 'error'
  | 'ready'

type AuthContextValue = {
  phase: AuthPhase
  me: MeResponse | null
  errorMessage: string | null
  isAmplifyReady: boolean
  refresh: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  confirmRegistration: (email: string, code: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function resolveAuthState(): Promise<{
  phase: AuthPhase
  me: MeResponse | null
  errorMessage: string | null
}> {
  const session = await fetchAuthSession()
  if (!session.tokens?.idToken) {
    return { phase: 'unauthenticated', me: null, errorMessage: null }
  }

  try {
    await maruyaApi<ProvisionResponse>('/v1/users/me/provision', {
      method: 'POST',
    })
    const mePayload = await maruyaApi<MeResponse>('/v1/me')
    return {
      phase: 'ready',
      me: normalizeMeResponse(mePayload),
      errorMessage: null,
    }
  } catch (error) {
    if (error instanceof MaruyaApiError) {
      if (error.body.error === 'provision_denied') {
        return { phase: 'needs_invite', me: null, errorMessage: null }
      }
      if (error.body.error === 'email_required') {
        return {
          phase: 'email_required',
          me: null,
          errorMessage: error.body.message ?? 'メールアドレスが必要です',
        }
      }
    }
    const message =
      error instanceof Error ? error.message : '認証状態の取得に失敗しました'
    return { phase: 'error', me: null, errorMessage: message }
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const isAmplifyReady = configureAmplify()
  const [phase, setPhase] = useState<AuthPhase>('loading')
  const [me, setMe] = useState<MeResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!isAmplifyReady) {
      setPhase('error')
      setMe(null)
      setErrorMessage(
        'Cognito 設定がありません。amplify_outputs.json または VITE_COGNITO_* を設定してください。',
      )
      return
    }

    setPhase('loading')
    setErrorMessage(null)
    const next = await resolveAuthState()
    setPhase(next.phase)
    setMe(next.me)
    setErrorMessage(next.errorMessage)
  }, [isAmplifyReady])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        await signIn({ username: email, password })
      } catch (error) {
        if (error instanceof Error && error.name === 'UserAlreadyAuthenticatedException') {
          // ブラウザに残った別ユーザー/古いセッションが残留しているケース。
          // クライアント側で手動ログアウトできないため、ここで自動的に破棄して再試行する。
          await signOut()
          await signIn({ username: email, password })
        } else {
          throw error
        }
      }
      await refresh()
    },
    [refresh],
  )

  const register = useCallback(async (email: string, password: string) => {
    await signUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    })
  }, [])

  const confirmRegistration = useCallback(async (email: string, code: string) => {
    await confirmSignUp({ username: email, confirmationCode: code })
  }, [])

  const logout = useCallback(async () => {
    await signOut()
    setPhase('unauthenticated')
    setMe(null)
    setErrorMessage(null)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      phase,
      me,
      errorMessage,
      isAmplifyReady,
      refresh,
      login,
      register,
      confirmRegistration,
      logout,
    }),
    [
      phase,
      me,
      errorMessage,
      isAmplifyReady,
      refresh,
      login,
      register,
      confirmRegistration,
      logout,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
